"use client";

import React from "react";
import { GuideElement, type GuideElementProps } from "../BaseDemoElement";
import { Search, Users } from "lucide-react";
import { useResponsiveCardStyle, type CardSize } from "@/hooks/use-responsive-card-style";
import { useIsMobile } from "@/hooks/use-breakpoint";

interface BrowseCreatorsProps extends Omit<GuideElementProps, "title" | "description" | "icon" | "targetSelector"> {
  role: "brand" | "creator";
  onRoleChange?: (role: "brand" | "creator") => void;
  selectedRole?: "brand" | "creator";
  size?: CardSize;
}

export function BrowseCreatorsStep({ role, onRoleChange, selectedRole, size = "md", ...rest }: BrowseCreatorsProps) {
  // Get responsive styles using centralized hook
  const cardStyle = useResponsiveCardStyle(size, "wide");
  const isMobile = useIsMobile();

  const description =
    role === "brand"
      ? "Browse and shortlist creators. Use filters to match audience, category and performance."
      : "Explore brands' favorite creators and see where your profile could fit best.";

  return (
    <GuideElement
      title={role === "brand" ? "Discover creators" : "Explore creators"}
      description={description}
      icon={role === "brand" ? Search : Users}
      targetSelector={isMobile ? undefined : '[data-guide-step="creators-list"]'}
      position={isMobile ? "center" : "left"}
      spotlight={true}
      forceSpotlight={!isMobile}
      offset={{ x: 12, y: 0 }}
      showArrow
      disableAutoScroll={isMobile}
      computePosition={isMobile ? undefined : ({ getSpotlightBounds, defaultWidth, defaultHeight, margin }) => {
        const s = getSpotlightBounds();
        const ox = rest.offset && typeof rest.offset.x === 'number' ? rest.offset.x : 0;
        const oy = rest.offset && typeof rest.offset.y === 'number' ? rest.offset.y : 0;
        return {
          top: s.top + s.height / 2 - defaultHeight / 2 + oy,
          left: s.left - defaultWidth - margin + ox,
          transform: 'none',
          tooltipWidth: defaultWidth,
          tooltipHeight: defaultHeight,
        };
      }}
      style={{ ...cardStyle, ...(rest.style || {}) }}
      {...rest}
    >
      <div className="pt-1">
        <div className="inline-flex items-center rounded-full border border-slate-300 p-0.5 text-xs">
          <button
            onClick={() => onRoleChange?.("brand")}
            className={`px-2 py-1 rounded-full ${selectedRole === "brand" ? "bg-blue-600 text-white" : "text-slate-700"}`}
          >
            Brand
          </button>
          <button
            onClick={() => onRoleChange?.("creator")}
            className={`px-2 py-1 rounded-full ${selectedRole === "creator" ? "bg-blue-600 text-white" : "text-slate-700"}`}
          >
            Creator
          </button>
        </div>
      </div>
    </GuideElement>
  );
}

export default BrowseCreatorsStep;


